import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, AlertCircle, Zap, Loader2 } from 'lucide-react';
import { db, auth } from '../firebase';
import { collection, query, getDocs, where, limit, doc, setDoc, orderBy } from 'firebase/firestore';

const SEARCH_LIMIT = 50;

export const CollegeSelectionPage = () => {
  const navigate = useNavigate();
  
  const [searchQuery, setSearchQuery] = useState('');
  const [colleges, setColleges] = useState([]);
  const [selectedCollege, setSelectedCollege] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Debounced search
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchColleges(searchQuery);
    }, 400);
    return () => clearTimeout(delayDebounceFn);
  }, [searchQuery]);

  const fetchColleges = async (queryText) => {
    if (!queryText || !queryText.trim()) {
      setColleges([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const queryLower = queryText.toLowerCase().trim();

      const collegesQuery = query(
        collection(db, 'colleges'),
        where('searchName', '>=', queryLower),
        where('searchName', '<=', queryLower + '\uf8ff'),
        orderBy('searchName', 'asc'),
        limit(SEARCH_LIMIT)
      );
      
      const collegesSnapshot = await getDocs(collegesQuery);
      const collegesList = collegesSnapshot.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => {
          if (a.isBubble && !b.isBubble) return -1;
          if (!a.isBubble && b.isBubble) return 1;
          return a.name.localeCompare(b.name);
        });

      setColleges(collegesList);
    } catch (err) {
      // console.error(err);
      setError('Could not search colleges. Please check your internet connection.');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCollege = async () => {
    if (!selectedCollege || saving) return;

    try {
      setSaving(true);
      const user = auth.currentUser;
      if (!user) throw new Error('User not authenticated');

      const userDocRef = doc(db, 'users', user.uid);
      await setDoc(userDocRef, { college: selectedCollege.name }, { merge: true });

      const redirect = new URLSearchParams(window.location.search).get('redirect');
      navigate(redirect ? `/onboarding?redirect=${encodeURIComponent(redirect)}` : '/onboarding');
    } catch (err) {
      setError('Could not save your college. Please try again.');
      setSaving(false);
    }
  };

  const HighlightedText = ({ text, highlight }) => {
    if (!highlight) return <span>{text}</span>;
    const parts = text.split(new RegExp(`(${highlight})`, 'gi'));
    return (
      <span>
        {parts.map((part, i) => 
          part.toLowerCase() === highlight.toLowerCase() ? 
            <span key={i} className="text-[#0BD9E5] bg-[#0BD9E5]/10">{part}</span> : part
        )}
      </span>
    );
  };

  return (
    <div className="flex flex-col min-h-screen bg-[#0a0a0a] text-white font-poppins relative">
      
      {/* Background Effects */}
      <div className="absolute inset-0 z-0 pointer-events-none">
         <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-[500px] bg-[#0BD9E5]/5 blur-[120px] rounded-full mix-blend-screen" />
      </div>

      <div className="relative z-10 flex-1 flex flex-col px-4 pt-12 pb-6 max-w-2xl mx-auto w-full">
        
        {/* Header */}
        <div className="text-center mb-10">
            <h1 className="text-3xl font-bold tracking-tight mb-2">Pick Your College</h1>
            <p className="text-gray-400 text-sm">Where are you studying?</p>
        </div>

        {/* Search Input */}
        <div className="relative mb-6">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <Search size={20} className="text-gray-500" />
            </div>
            <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Hunt your college..."
                className="w-full bg-[#1a1a1a] border border-gray-800 rounded-xl py-4 pl-12 pr-4 text-white focus:outline-none focus:border-[#0BD9E5] transition-colors"
                autoFocus
            />
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto no-scrollbar pb-24 relative">
            
            {error && (
                <div className="bg-red-500/10 border border-red-500/50 p-4 rounded-xl text-red-500 text-sm mb-4 text-center">
                    {error}
                </div>
            )}

            {!searchQuery.trim() ? (
                // Instructions State
                <div className="bg-[#161616] border border-[#222] rounded-2xl p-6 shadow-xl">
                    <div className="flex items-center gap-3 mb-6">
                        <AlertCircle className="text-[#0BD9E5]" size={24} />
                        <h2 className="text-lg font-semibold">Important Instructions</h2>
                    </div>
                    <ul className="space-y-4">
                        <li className="flex gap-3 text-sm text-gray-300">
                            <span className="text-[#0BD9E5] text-lg leading-none">•</span>
                            <span className="leading-relaxed">Select your college carefully. This selection will be permanently linked to the College ID you used during login.</span>
                        </li>
                        <li className="flex gap-3 text-sm text-gray-300">
                            <span className="text-[#0BD9E5] text-lg leading-none">•</span>
                            <span className="leading-relaxed">If your selected college does not match your College ID, your account will be terminated within a few hours.</span>
                        </li>
                        <li className="flex gap-3 text-sm text-gray-300">
                            <span className="text-[#0BD9E5] text-lg leading-none">•</span>
                            <span className="leading-relaxed">Can't find your college? Submit a request at <a href="https://www.sparkpl.com" target="_blank" rel="noreferrer" className="text-[#0BD9E5] hover:underline">sparkpl.com</a> and we'll add it within 24 hours.</span>
                        </li>
                    </ul>
                </div>
            ) : (
                // Results State
                <>
                    {loading ? (
                        <div className="flex justify-center p-12">
                            <Loader2 className="w-8 h-8 text-[#0BD9E5] animate-spin" />
                        </div>
                    ) : colleges.length > 0 ? (
                        <div className="space-y-3">
                            {colleges.map((college) => {
                                const isSelected = selectedCollege?.id === college.id;
                                return (
                                    <button
                                        key={college.id}
                                        onClick={() => setSelectedCollege(isSelected ? null : college)}
                                        className={`w-full text-left p-4 rounded-xl border transition-all duration-200 ${
                                            isSelected 
                                            ? 'bg-[#0BD9E5]/10 border-[#0BD9E5] shadow-[0_0_15px_rgba(11,217,229,0.15)]' 
                                            : 'bg-[#1a1a1a] border-[#333] hover:border-gray-600'
                                        }`}
                                    >
                                        <div className="font-medium text-[15px]">
                                            <HighlightedText text={college.name} highlight={searchQuery} />
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="text-center p-8 mt-4">
                            <p className="text-gray-300 font-medium mb-6">No hits found for "{searchQuery}"</p>
                            <div className="flex flex-col gap-3 text-sm text-gray-500">
                                <p>• Double-check your spelling.</p>
                                <p>• Still can't find it? Submit a request at <a href="https://www.sparkpl.com" target="_blank" rel="noreferrer" className="text-[#0BD9E5] hover:underline">sparkpl.com</a>.</p>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>

        {/* Save Button (Fixed at bottom) */}
        <AnimatePresence>
            {selectedCollege && (
                <motion.div 
                    initial={{ y: 100, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: 100, opacity: 0 }}
                    className="absolute bottom-6 left-4 right-4 z-20 flex justify-center"
                >
                    <button
                        onClick={handleSaveCollege}
                        disabled={saving}
                        className="w-full max-w-sm bg-white text-black font-bold uppercase tracking-widest py-4 rounded-xl flex items-center justify-center gap-2 hover:bg-gray-200 transition-colors disabled:opacity-50 shadow-[0_4px_15px_rgba(0,0,0,0.5)] overflow-hidden relative group"
                    >
                        {saving ? (
                            <Loader2 className="w-6 h-6 animate-spin" />
                        ) : (
                            <>
                                <span className="relative z-10">Slay the Start</span>
                                <Zap className="w-5 h-5 relative z-10" fill="currentColor" />
                                
                                {/* Hover Effect */}
                                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-black/5 to-transparent -translate-x-full group-hover:animate-[shimmer_1.5s_infinite]" />
                            </>
                        )}
                    </button>
                </motion.div>
            )}
        </AnimatePresence>

      </div>
    </div>
  );
};

export default CollegeSelectionPage;

